import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus, Search, X, ArrowLeft, Pin, BellOff } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  useConversations,
  useMarkConversationRead,
  useCreateConversation,
  useGlobalSearch,
  conversationLabel,
  type Conversation,
} from '@/hooks/data/useChat';
import { useAgents } from '@/hooks/data/useAgents';
import { useAuth } from '@/hooks/useAuth';
import { ChatRoom } from '@/components/chat/ChatRoom';
import { toast } from 'sonner';
import { formatDate } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/communication')({
  component: CommunicationPage,
});

function CommunicationPage() {
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const { data: conversations = [], isLoading } = useConversations();
  const markRead = useMarkConversationRead();
  const [active, setActive] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [globalQuery, setGlobalQuery] = useState('');
  const { data: searchResults = [], isFetching: searching } =
    useGlobalSearch(globalQuery);

  // Auto-select first conversation on desktop
  useEffect(() => {
    if (
      !active &&
      conversations.length > 0 &&
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches
    ) {
      setActive(conversations[0]!.id);
    }
  }, [active, conversations]);

  useEffect(() => {
    if (active) markRead.mutate(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Sort: pinned (system) → most recent
  const sorted = useMemo(
    () =>
      [...conversations].sort((a, b) => {
        if (a.system_role && !b.system_role) return -1;
        if (!a.system_role && b.system_role) return 1;
        return (
          (b.last_message_at ? Number(b.last_message_at) : 0) -
          (a.last_message_at ? Number(a.last_message_at) : 0)
        );
      }),
    [conversations],
  );

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t('chat.title', 'Kommunikation')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('chat.subtitle', 'Team-Chat mit Echtzeit-Synchronisation.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setComposeOpen(true)}
          className="inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold text-[#1a1305] shadow-soft transition-transform hover:scale-[1.02]"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
          }}
        >
          <MessageSquarePlus className="h-4 w-4" />
          {t('chat.new', 'Neue Konversation')}
        </button>
      </header>

      <div
        className={`grid gap-4 ${
          active ? 'md:grid-cols-[280px_1fr]' : 'md:grid-cols-[280px_1fr]'
        }`}
      >
        <aside
          className={`flex flex-col gap-2 rounded-2xl border border-line bg-card p-2 shadow-soft ${
            active ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* Global cross-conversation search */}
          <div className="relative px-1 pt-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
            <input
              type="search"
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              placeholder={t(
                'chat.global_search.placeholder',
                'In allen Konversationen suchen …',
              )}
              className="w-full rounded-md border border-line bg-paper-2 py-2 ps-9 pe-8 text-sm text-ink outline-none transition-colors placeholder:text-ink-3 focus:border-[hsl(var(--brand-gold))]"
            />
            {globalQuery && (
              <button
                type="button"
                onClick={() => setGlobalQuery('')}
                className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-3 hover:text-ink"
                aria-label={t('chat.global_search.close', 'Suche schließen')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Search results override the conversation list while query active */}
          {globalQuery.trim().length >= 2 ? (
            <div className="flex-1 overflow-y-auto">
              {searching && (
                <p className="px-3 py-3 text-xs text-ink-3">
                  {t('common.searching', 'Suche …')}
                </p>
              )}
              {!searching && searchResults.length === 0 && (
                <p className="px-3 py-3 text-xs text-ink-3">
                  {t('chat.global_search.empty', 'Keine Treffer.')}
                </p>
              )}
              {searchResults.map((r) => {
                const conv = sorted.find((c) => c.id === r.conversationId);
                const convLabel = conv
                  ? conversationLabel(conv, profile?.id, i18n.language)
                  : '—';
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setActive(r.conversationId);
                      setGlobalQuery('');
                    }}
                    className="block w-full rounded-md px-3 py-2 text-start transition-colors hover:bg-paper-2"
                  >
                    <p className="truncate text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--brand-gold-deep))]">
                      {convLabel}
                    </p>
                    <p className="line-clamp-2 text-xs text-ink">{r.message}</p>
                    <p className="text-[10px] text-ink-3">
                      {formatDate(r.createdAt)}
                    </p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex-1 space-y-1 overflow-y-auto">
          {isLoading && <p className="px-3 py-2 text-sm text-muted-foreground">Lädt …</p>}
          {!isLoading && sorted.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {t('chat.empty', 'Keine Konversationen.')}
            </p>
          )}
          {sorted.map((c) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              active={c.id === active}
              currentUserId={profile?.id}
              lang={i18n.language}
              onClick={() => setActive(c.id)}
            />
          ))}
            </div>
          )}
        </aside>

        <div className={`min-w-0 ${active ? '' : 'hidden md:block'}`}>
          {active ? (
            <div>
              {/* Mobile back button */}
              <button
                type="button"
                onClick={() => setActive(null)}
                className="mb-2 flex items-center gap-2 text-sm text-ink-2 md:hidden"
              >
                <ArrowLeft className="h-4 w-4" /> {t('chat.back_to_list', 'Konversationen')}
              </button>
              <ChatRoom conversationId={active} />
            </div>
          ) : (
            <div className="flex h-[600px] items-center justify-center rounded-2xl border border-dashed border-line text-muted-foreground">
              {t('chat.select', 'Konversation auswählen')}
            </div>
          )}
        </div>
      </div>

      {composeOpen && (
        <ComposeDialog
          onClose={() => setComposeOpen(false)}
          onCreated={(id) => {
            setComposeOpen(false);
            setActive(id);
          }}
        />
      )}
    </section>
  );
}

function ConversationRow({
  conversation,
  active,
  currentUserId,
  lang,
  onClick,
}: {
  conversation: Conversation;
  active: boolean;
  currentUserId: string | undefined;
  lang: string;
  onClick: () => void;
}) {
  const label = conversationLabel(conversation, currentUserId, lang);
  const initials =
    label
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('') || '?';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-start transition ${
        active
          ? 'bg-[hsl(var(--brand-gold)/0.12)] text-ink'
          : 'text-ink-2 hover:bg-paper-2'
      }`}
    >
      {conversation.system_role === 'support' ? (
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-gold)/0.18)] text-[hsl(var(--brand-gold-deep))]">
          <Pin className="h-4 w-4" />
        </span>
      ) : (
        <Avatar
          name={label}
          src={conversation.participants.find((p) => p.id !== currentUserId)?.avatar_url}
          size="sm"
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1 truncate text-sm font-medium text-ink">
          <span className="truncate">{label}</span>
          {conversation.muted && <BellOff className="h-3 w-3 shrink-0 opacity-60" />}
        </p>
        <p className="truncate text-xs text-ink-3">
          {conversation.last_message_at
            ? formatDate(conversation.last_message_at)
            : '—'}
        </p>
      </div>
      {conversation.unread_count > 0 && (
        <Badge
          className={
            conversation.muted
              ? 'bg-paper-3 text-ink-3'
              : 'bg-[hsl(var(--brand-gold))] text-[#1a1305]'
          }
        >
          {conversation.unread_count}
        </Badge>
      )}
    </button>
  );
}

function ComposeDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: agents = [] } = useAgents();
  const create = useCreateConversation();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState('');

  const composable = useMemo(
    () => agents.filter((a) => a.id !== profile?.id && a.status === 'active'),
    [agents, profile?.id],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return composable;
    return composable.filter(
      (a) =>
        a.full_name.toLowerCase().includes(q) ||
        a.agent_id?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q),
    );
  }, [composable, query]);

  const togglePick = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onCreate = async () => {
    if (picked.size === 0) {
      toast.error(t('chat.compose.no_picks', 'Mindestens einen Teilnehmer wählen.'));
      return;
    }
    try {
      const created = await create.mutateAsync({
        type: picked.size > 1 ? 'group' : 'direct',
        name: picked.size > 1 ? groupName.trim() || undefined : undefined,
        participantProfileIds: Array.from(picked),
      });
      onCreated(created.id);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl border border-line bg-card shadow-elevated">
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h3 className="font-display text-lg text-ink">
            {t('chat.compose.title', 'Neue Konversation')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-ink-3 hover:bg-paper-2 hover:text-ink"
            aria-label={t('common.close', 'Schließen')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="border-b border-line bg-paper-2 px-3 py-2">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 flex-shrink-0 text-ink-3" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('chat.compose.search', 'Mitarbeiter suchen …')}
              className="w-full border-0 bg-transparent text-sm text-ink outline-none placeholder:text-ink-3"
            />
          </div>
        </div>

        <div className="max-h-[40vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-ink-3">
              {t('chat.compose.no_results', 'Keine Treffer')}
            </p>
          ) : (
            filtered.map((a) => {
              const isPicked = picked.has(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => togglePick(a.id)}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-start text-sm transition-colors hover:bg-paper-2 ${
                    isPicked ? 'bg-[hsl(var(--brand-gold)/0.1)]' : ''
                  }`}
                >
                  <Avatar name={a.full_name} src={a.avatar_url} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{a.full_name}</p>
                    <p className="font-mono text-[11px] text-ink-3">
                      {a.agent_id} · {a.role}
                    </p>
                  </div>
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded border ${
                      isPicked
                        ? 'border-transparent bg-[hsl(var(--brand-gold))] text-[#1a1305]'
                        : 'border-line'
                    }`}
                  >
                    {isPicked && '✓'}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {picked.size > 1 && (
          <div className="border-t border-line px-4 py-3">
            <label className="block text-xs font-medium uppercase tracking-[0.08em] text-ink-3">
              {t('chat.compose.group_name', 'Gruppenname (optional)')}
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('chat.compose.group_placeholder', 'z. B. Team Wien-West')}
              className="mt-1 w-full rounded-md border border-line bg-paper px-3 py-1.5 text-sm text-ink outline-none focus:border-[hsl(var(--brand-gold))]"
            />
          </div>
        )}

        <footer className="flex items-center justify-between gap-2 border-t border-line px-4 py-3">
          <span className="text-xs text-ink-3">
            {picked.size === 0
              ? t('chat.compose.pick_hint', 'Wählen Sie Empfänger')
              : picked.size === 1
                ? t('chat.compose.direct_hint', 'Direktnachricht')
                : t('chat.compose.group_hint', '{{count}} Teilnehmer · Gruppe', {
                    count: picked.size,
                  })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line bg-card px-3 py-1.5 text-sm text-ink-2 hover:bg-paper-2"
            >
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button
              type="button"
              onClick={onCreate}
              disabled={picked.size === 0 || create.isPending}
              className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold text-[#1a1305] shadow-soft transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
              style={{
                background:
                  'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
              }}
            >
              {create.isPending
                ? t('chat.compose.creating', 'Erstelle …')
                : t('chat.compose.create', 'Erstellen')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
